import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/primitives";
import { Funnel, ArrowClockwise, ArrowLeft, ArrowRight } from "@phosphor-icons/react";

const ENTITY_LABELS = {
  "":                "All entities",
  "user":            "Users",
  "organization":    "Organizations",
  "purchase-order":  "Purchase orders",
  "request":         "Requests",
  "inventory":       "Inventory",
  "employee":        "Employees",
  "payroll":         "Payroll",
  "invoice":         "Invoices",
  "machine":         "Machines",
};

const ACTION_TONE = {
  create:   "bg-[#dcfce7] text-[#166534]",
  update:   "bg-[#dbeafe] text-[#1e40af]",
  delete:   "bg-[#fee2e2] text-[#991b1b]",
  approve:  "bg-[#dcfce7] text-[#166534]",
  reject:   "bg-[#fee2e2] text-[#991b1b]",
  adjust:   "bg-[#fef3c7] text-[#92400e]",
  archive:  "bg-[#f3f4f6] text-[#374151]",
  restore:  "bg-[#dcfce7] text-[#166534]",
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "medium" });
  } catch { return iso; }
}

function actionTone(action) {
  const verb = (action || "").split(".").pop();
  return ACTION_TONE[verb] || "bg-[#f4f4f5] text-[#374151]";
}

function DiffCell({ before, after }) {
  const beforeStr = before ? JSON.stringify(before) : "";
  const afterStr  = after  ? JSON.stringify(after)  : "";
  if (!beforeStr && !afterStr) return <span className="text-[#a1a1aa]">—</span>;
  return (
    <div className="mono text-[10px] leading-relaxed">
      {beforeStr && (
        <div className="text-[#991b1b] line-through decoration-[#fee2e2]">− {beforeStr}</div>
      )}
      {afterStr && (
        <div className="text-[#166534]">+ {afterStr}</div>
      )}
    </div>
  );
}

export default function ActivityLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 50;

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, skip });
      if (entityType) params.set("entity_type", entityType);
      if (action)     params.set("action", action);
      const { data } = await api.get(`/activity-log?${params}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, action, skip]);

  const page = Math.floor(skip / LIMIT) + 1;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div data-testid="activity-log-page">
      <PageHeader
        overline="Control Room / Security"
        title="Activity log"
        subtitle="Immutable audit trail of every important write across the platform."
        testId="activity-log-header"
      />

      <div className="phizo-card p-4 mb-4 flex items-center gap-3 flex-wrap">
        <Funnel size={16} weight="bold" className="text-[#71717a]" />
        <select
          value={entityType}
          onChange={(e) => { setEntityType(e.target.value); setSkip(0); }}
          className="border border-[#e4e4e7] bg-white px-3 py-1.5 text-xs outline-none focus:border-black"
          data-testid="activity-filter-entity"
        >
          {Object.entries(ENTITY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <input
          type="text"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setSkip(0); load(); } }}
          placeholder="Filter action (e.g. po.approve)"
          className="border border-[#e4e4e7] bg-white px-3 py-1.5 text-xs outline-none focus:border-black w-64"
          data-testid="activity-filter-action"
        />
        <button
          type="button"
          onClick={() => { setSkip(0); load(); }}
          className="border border-[#e4e4e7] px-3 py-1.5 text-xs hover:bg-[#f4f4f5]"
          data-testid="activity-refresh"
        >
          <ArrowClockwise size={12} weight="bold" className="inline mr-1" />
          Refresh
        </button>
        <div className="ml-auto text-xs text-[#71717a] mono" data-testid="activity-total">
          {total.toLocaleString()} entries
        </div>
      </div>

      <div className="phizo-card overflow-x-auto" data-testid="activity-table">
        <table className="w-full text-xs">
          <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
            <tr className="text-left">
              <th className="px-3 py-2 phizo-label text-[9px]">When</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Actor</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Action</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Entity</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Change</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-[#71717a]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-[#71717a]">No activity matches your filter.</td></tr>
            )}
            {rows.map((r, idx) => (
              <tr key={r.id || idx} className="border-b border-[#f4f4f5] hover:bg-[#fafafa]"
                  data-testid={`activity-row-${idx}`}>
                <td className="px-3 py-2 whitespace-nowrap mono text-[11px]">{fmtWhen(r.at)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="font-semibold">{r.actor_name || r.actor_username || "—"}</div>
                  <div className="mono text-[10px] text-[#71717a]">{r.actor_role || ""}</div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={`inline-block mono text-[10px] px-1.5 py-0.5 ${actionTone(r.action)}`}>
                    {r.action}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="phizo-label text-[9px] text-[#a1a1aa]">{r.entity_type}</div>
                  <div className="font-semibold truncate max-w-[220px]" title={r.entity_label || r.entity_id}>
                    {r.entity_label || (r.entity_id || "").slice(0, 8) || "—"}
                  </div>
                </td>
                <td className="px-3 py-2 max-w-[520px]">
                  <DiffCell before={r.before} after={r.after} />
                  {r.details && (
                    <div className="mono text-[10px] text-[#71717a] mt-0.5">
                      {JSON.stringify(r.details)}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4 text-xs text-[#71717a]">
        <div>Page {page} of {pages}</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={skip === 0}
            onClick={() => setSkip((s) => Math.max(0, s - LIMIT))}
            className="border border-[#e4e4e7] px-3 py-1.5 disabled:opacity-40 hover:bg-[#f4f4f5]"
            data-testid="activity-prev"
          >
            <ArrowLeft size={12} weight="bold" className="inline mr-1" /> Prev
          </button>
          <button
            type="button"
            disabled={skip + LIMIT >= total}
            onClick={() => setSkip((s) => s + LIMIT)}
            className="border border-[#e4e4e7] px-3 py-1.5 disabled:opacity-40 hover:bg-[#f4f4f5]"
            data-testid="activity-next"
          >
            Next <ArrowRight size={12} weight="bold" className="inline ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
}
